import { useCallback, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import api, { formatApiError } from '@/lib/api';
import { toast } from '@/components/ui/sonner';

const MILES_PER_METER = 0.000621371;

function distanceMiles(a, b) {
  if (!a || !b) return 0;
  const toRad = (value) => Number(value) * Math.PI / 180;
  const radiusMeters = 6371000;
  const dLat = toRad(b.latitude - a.latitude);
  const dLon = toRad(b.longitude - a.longitude);
  const lat1 = toRad(a.latitude);
  const lat2 = toRad(b.latitude);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return (radiusMeters * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h))) * MILES_PER_METER;
}

const statusColors = {
  tentative: 'bg-blue-50 text-blue-700 border border-blue-200',
  scheduled: 'bg-white text-zinc-800 border border-zinc-300',
  in_progress: 'bg-amber-100 text-amber-700',
  completed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-zinc-100 text-zinc-600',
};

const defaultActualCosts = { final_invoice_amount: '', actual_dump_fee: '', actual_fuel_cost: '', actual_labor_hours: '', actual_workers: 1, actual_lead_cost: '', actual_rental_cost: '', actual_misc_cost: '' };

export function useStaffViewModel() {
  const { user, logout } = useAuth();
  const [viewDate, setViewDate] = useState(new Date());
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeJob, setActiveJob] = useState(null);
  const [etaInput, setEtaInput] = useState('15 minutes');
  const [checkoutNotes, setCheckoutNotes] = useState('');
  const [actualCosts, setActualCosts] = useState(defaultActualCosts);
  const [noteText, setNoteText] = useState('');
  const [showExpense, setShowExpense] = useState(false);
  const [showMileage, setShowMileage] = useState(false);
  const [showLineItems, setShowLineItems] = useState(false);
  const [lineItems, setLineItems] = useState([]);
  const [showSms, setShowSms] = useState(null);
  const [smsText, setSmsText] = useState('');
  const [expenses, setExpenses] = useState([]);
  const [mileageLogs, setMileageLogs] = useState([]);
  const [expenseSubmitting, setExpenseSubmitting] = useState(false);
  const [expenseError, setExpenseError] = useState('');
  const [mileageSubmitting, setMileageSubmitting] = useState(false);
  const [mileageError, setMileageError] = useState('');
  const [mileageEndValues, setMileageEndValues] = useState({});
  const [gpsMileage, setGpsMileage] = useState({ active: false, jobId: '', points: [], distance: 0, watchId: null, error: '' });
  const [jobPhotos, setJobPhotos] = useState([]);
  const [checkoutError, setCheckoutError] = useState('');
  const [etaStatus, setEtaStatus] = useState(null);
  const [notifyCustomerEta, setNotifyCustomerEta] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [paymentMessage, setPaymentMessage] = useState('');

  const dateStr = format(viewDate, 'yyyy-MM-dd');

  const fetchRoute = useCallback(async () => {
    try {
      const { data } = await api.get('/staff/my-route', { params: { date: dateStr } });
      setRoute(data);
    } catch { return; }
    finally { setLoading(false); }
  }, [dateStr]);

  const fetchExpenses = useCallback(async () => {
    try { const { data } = await api.get('/expenses'); setExpenses(data); }
    catch { return; }
  }, []);

  const fetchMileage = useCallback(async () => {
    try { const { data } = await api.get('/mileage'); setMileageLogs(data); }
    catch { return; }
  }, []);

  useEffect(() => { fetchRoute(); }, [fetchRoute]);
  useEffect(() => { fetchExpenses(); }, [fetchExpenses]);
  useEffect(() => { fetchMileage(); }, [fetchMileage]);

  const checkin = useCallback(async (jobId, action, extra = {}) => {
    setCheckoutError('');
    try {
      const { data } = await api.post(`/jobs/${jobId}/checkin`, { action, ...extra });
      if (action === 'on_the_way') setEtaStatus(data.job?.eta_status || data.sms_result || null);
      fetchRoute();
      if (activeJob?.id === jobId) setActiveJob(data.job);
      if (action === 'on_site') toast.success('On-site status saved');
      if (action === 'checkout') toast.success('Job closeout saved');
      return data;
    } catch (error) {
      const detail = error.response?.data?.detail;
      setCheckoutError(formatApiError(detail?.message || detail));
      throw error;
    }
  }, [activeJob?.id, fetchRoute]);

  const updateActualCost = useCallback((key, value) => setActualCosts(prev => ({ ...prev, [key]: value })), []);

  const getCurrentLocation = useCallback(() => new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve({});
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => resolve({ latitude: position.coords.latitude, longitude: position.coords.longitude, captured_at: new Date().toISOString() }),
      () => resolve({}),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
    );
  }), []);

  const sendOnMyWay = useCallback(async () => {
    if (!activeJob) return;
    setEtaStatus({ loading: true, message: 'Capturing location and preparing ETA...' });
    const coords = await getCurrentLocation();
    const data = await checkin(activeJob.id, 'on_the_way', { eta: etaInput, notify_customer: notifyCustomerEta, ...coords });
    setEtaStatus({ ...(data.job?.eta_status || {}), sms_result: data.sms_result, has_location: Boolean(coords.latitude && coords.longitude) });
    toast.success('Starting ETA saved');
  }, [activeJob, checkin, etaInput, getCurrentLocation, notifyCustomerEta]);

  const currentEtaStatus = etaStatus || activeJob?.eta_status;
  const currentEtaSmsResult = etaStatus?.sms_result || null;
  const lineItemTotal = useMemo(() => lineItems.reduce((sum, item) => sum + (Number(item.quantity) || 0) * (Number(item.unit_price) || 0), 0), [lineItems]);

  const refreshActiveJob = useCallback(async (jobId) => {
    const { data } = await api.get(`/jobs/${jobId}`);
    setActiveJob(data);
    setActualCosts({
      final_invoice_amount: data.actual_costs?.final_invoice_amount || data.estimated_value || '',
      actual_dump_fee: data.actual_costs?.actual_dump_fee || '',
      actual_fuel_cost: data.actual_costs?.actual_fuel_cost || '',
      actual_labor_hours: data.actual_costs?.actual_labor_hours || '',
      actual_workers: data.actual_costs?.actual_workers || data.assigned_staff?.length || 1,
      actual_lead_cost: data.actual_costs?.actual_lead_cost || '',
      actual_rental_cost: data.actual_costs?.actual_rental_cost || '',
      actual_misc_cost: data.actual_costs?.actual_misc_cost || '',
    });
    return data;
  }, []);

  const addNote = useCallback(async () => {
    if (!noteText.trim() || !activeJob) return;
    await api.post(`/jobs/${activeJob.id}/notes`, { text: noteText });
    setNoteText('');
    fetchRoute();
    await refreshActiveJob(activeJob.id);
    toast.success('Note saved');
  }, [activeJob, fetchRoute, noteText, refreshActiveJob]);

  const saveLineItems = useCallback(async () => {
    if (!activeJob) return;
    const { data } = await api.put(`/jobs/${activeJob.id}/line-items`, { line_items: lineItems });
    setActiveJob(data);
    setShowLineItems(false);
    fetchRoute();
    toast.success('Pricing saved');
  }, [activeJob, fetchRoute, lineItems]);

  const submitExpense = useCallback(async (event) => {
    event.preventDefault();
    setExpenseSubmitting(true);
    setExpenseError('');
    try {
      const formData = new FormData(event.target);
      formData.set('date', dateStr);
      formData.set('job_id', activeJob?.id || '');
      formData.set('dba_id', activeJob?.dba_id || '');
      await api.post('/expenses/with-receipt', formData);
      event.target.reset();
      setShowExpense(false);
      fetchExpenses();
      toast.success('Expense saved');
    } catch (error) {
      setExpenseError(formatApiError(error.response?.data?.detail));
    } finally {
      setExpenseSubmitting(false);
    }
  }, [activeJob, dateStr, fetchExpenses]);

  const submitMileage = useCallback(async (event) => {
    event.preventDefault();
    setMileageSubmitting(true);
    setMileageError('');
    const formData = new FormData(event.target);
    try {
      const rawEndOdometer = formData.get('odometer_end');
      const hasEndOdometer = rawEndOdometer !== null && String(rawEndOdometer).trim() !== '';
      const gpsPoints = gpsMileage.jobId === activeJob?.id ? gpsMileage.points : [];
      await api.post('/mileage', {
        date: formData.get('date') || dateStr,
        vehicle_label: formData.get('vehicle_label') || '',
        job_id: activeJob?.id || '',
        dba_id: activeJob?.dba_id || '',
        start_location: formData.get('start_location') || '',
        end_location: formData.get('end_location') || '',
        business_purpose: formData.get('business_purpose') || activeJob?.title || 'Business mileage',
        odometer_start: Number(formData.get('odometer_start')),
        odometer_end: hasEndOdometer ? Number(rawEndOdometer) : null,
        notes: formData.get('notes') || '',
        gps_start_latitude: gpsPoints[0]?.latitude ?? null,
        gps_start_longitude: gpsPoints[0]?.longitude ?? null,
        gps_end_latitude: gpsPoints[gpsPoints.length - 1]?.latitude ?? null,
        gps_end_longitude: gpsPoints[gpsPoints.length - 1]?.longitude ?? null,
        gps_distance_miles: Number(gpsMileage.distance || 0),
        gps_route_points: gpsPoints,
        distance_source: gpsPoints.length > 1 ? 'gps_assisted' : 'manual_odometer',
      });
      event.target.reset();
      setShowMileage(false);
      fetchMileage();
      toast.success(hasEndOdometer ? 'Mileage saved' : 'Starting mileage saved');
    } catch (error) {
      setMileageError(formatApiError(error.response?.data?.detail));
    } finally {
      setMileageSubmitting(false);
    }
  }, [activeJob, dateStr, fetchMileage, gpsMileage.distance, gpsMileage.jobId, gpsMileage.points]);

  const setMileageEndValue = useCallback((logId, value) => setMileageEndValues(prev => ({ ...prev, [logId]: value })), []);

  const saveMileageEnd = useCallback(async (log) => {
    const rawEnd = mileageEndValues[log.id];
    if (rawEnd === undefined || String(rawEnd).trim() === '') {
      toast.error('Enter ending mileage first');
      return;
    }
    try {
      await api.put(`/mileage/${log.id}`, {
        odometer_end: Number(rawEnd),
        end_location: log.end_location || activeJob?.address || '',
        gps_end_latitude: gpsMileage.jobId === log.job_id ? gpsMileage.points[gpsMileage.points.length - 1]?.latitude ?? null : null,
        gps_end_longitude: gpsMileage.jobId === log.job_id ? gpsMileage.points[gpsMileage.points.length - 1]?.longitude ?? null : null,
        gps_distance_miles: gpsMileage.jobId === log.job_id ? Number(gpsMileage.distance || 0) : null,
        gps_route_points: gpsMileage.jobId === log.job_id ? gpsMileage.points : [],
        distance_source: gpsMileage.jobId === log.job_id && gpsMileage.points.length > 1 ? 'gps_assisted' : 'manual_odometer',
      });
      setMileageEndValues(prev => {
        const next = { ...prev };
        delete next[log.id];
        return next;
      });
      await fetchMileage();
      toast.success('Ending mileage saved');
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  }, [activeJob?.address, fetchMileage, gpsMileage.distance, gpsMileage.jobId, gpsMileage.points, mileageEndValues]);

  const startGpsMileage = useCallback((job) => {
    if (!navigator.geolocation) {
      setGpsMileage(prev => ({ ...prev, error: 'GPS is not available on this device.' }));
      toast.error('GPS is not available on this device');
      return;
    }
    if (gpsMileage.watchId !== null) navigator.geolocation.clearWatch(gpsMileage.watchId);
    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        const nextPoint = { latitude: position.coords.latitude, longitude: position.coords.longitude, captured_at: new Date().toISOString() };
        setGpsMileage(prev => {
          const previousPoint = prev.points[prev.points.length - 1];
          const added = previousPoint ? distanceMiles(previousPoint, nextPoint) : 0;
          return { ...prev, active: true, jobId: job.id, points: [...prev.points, nextPoint].slice(-500), distance: Number((prev.distance + added).toFixed(2)), error: '' };
        });
      },
      () => setGpsMileage(prev => ({ ...prev, error: 'Unable to capture GPS for mileage.' })),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 15000 }
    );
    setGpsMileage({ active: true, jobId: job.id, points: [], distance: 0, watchId, error: '' });
    toast.success('GPS mileage tracking started');
  }, [gpsMileage.watchId]);

  const stopGpsMileage = useCallback(() => {
    if (gpsMileage.watchId !== null && navigator.geolocation) navigator.geolocation.clearWatch(gpsMileage.watchId);
    setGpsMileage(prev => ({ ...prev, active: false, watchId: null }));
    toast.success('GPS mileage tracking stopped');
  }, [gpsMileage.watchId]);

  useEffect(() => () => {
    if (gpsMileage.watchId !== null && navigator.geolocation) navigator.geolocation.clearWatch(gpsMileage.watchId);
  }, [gpsMileage.watchId]);

  const sendSmsToCustomer = useCallback(async () => {
    if (!smsText.trim() || !showSms) return;
    await api.post('/messages/send', { channel: 'sms', to: showSms.phone, text: smsText, customer_id: showSms.customer_id, dba_id: showSms.dba_id });
    setSmsText('');
    setShowSms(null);
    toast.success('SMS saved and sent');
  }, [showSms, smsText]);

  const fetchPaymentStatus = useCallback(async (jobId) => {
    if (!jobId) return;
    setPaymentError('');
    try {
      const { data } = await api.get(`/staff/jobs/${jobId}/payment-status`);
      setPaymentStatus(data);
      setPaymentMessage(data?.is_prepaid ? data.alert : '');
    } catch (error) {
      setPaymentError(formatApiError(error.response?.data?.detail));
    }
  }, []);

  const pollStripeStatus = useCallback(async (sessionId, attempts = 0) => {
    if (!sessionId || attempts >= 5) return;
    try {
      const { data } = await api.get(`/staff/payments/stripe/status/${sessionId}`);
      setPaymentStatus(data.payment_status);
      setPaymentMessage(data.payment_status?.is_prepaid ? data.payment_status.alert : 'Stripe payment is still pending.');
      if (data.payment_status?.is_prepaid) {
        if (activeJob?.id) {
          const refreshed = await api.get(`/jobs/${activeJob.id}`);
          setActiveJob(refreshed.data);
        }
        fetchRoute();
        return;
      }
      setTimeout(() => pollStripeStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      setPaymentError(formatApiError(error.response?.data?.detail));
    }
  }, [activeJob?.id, fetchRoute]);

  useEffect(() => {
    const rawSearch = window.location.search.replace(/^\?/, '');
    const queryPairs = rawSearch ? rawSearch.split('&').filter(Boolean).map((part) => part.split('=')) : [];
    const sessionPair = queryPairs.find(([key]) => decodeURIComponent(key || '') === 'stripe_session_id');
    const sessionId = sessionPair ? decodeURIComponent((sessionPair[1] || '').replace(/\+/g, ' ')) : '';
    if (sessionId) {
      setPaymentMessage('Checking Stripe payment status...');
      pollStripeStatus(sessionId);
      const nextSearch = queryPairs.filter(([key]) => decodeURIComponent(key || '') !== 'stripe_session_id').map(([key, value]) => `${key}${value !== undefined ? `=${value}` : ''}`).join('&');
      const next = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}`;
      window.history.replaceState({}, '', next);
    }
  }, [pollStripeStatus]);

  const recordPayment = useCallback(async ({ method, amount, note, recordOnly = false }) => {
    if (!activeJob) return;
    setPaymentLoading(true);
    setPaymentError('');
    setPaymentMessage('');
    try {
      const { data } = await api.post(`/staff/jobs/${activeJob.id}/payments`, { method, amount, note, record_only: recordOnly, origin_url: window.location.origin });
      setPaymentStatus(data);
      setPaymentMessage(data.message || (data.is_prepaid ? data.alert : 'Payment recorded.'));
      if (data.payment_link_url && method === 'square' && !recordOnly) window.open(data.payment_link_url, '_blank', 'noopener,noreferrer');
      if (data.payment_link_url && method === 'stripe' && !recordOnly) {
        window.open(data.payment_link_url, '_blank', 'noopener,noreferrer');
        if (data.stripe_session_id) pollStripeStatus(data.stripe_session_id);
      }
      const refreshed = await api.get(`/jobs/${activeJob.id}`);
      setActiveJob(refreshed.data);
      fetchRoute();
      toast.success(data.message || 'Payment record saved');
    } catch (error) {
      setPaymentError(formatApiError(error.response?.data?.detail));
    } finally {
      setPaymentLoading(false);
    }
  }, [activeJob, fetchRoute, pollStripeStatus]);

  const handleJobPhotosChange = useCallback((stagePhotos, allPhotos) => setJobPhotos(allPhotos || []), []);

  const openJob = useCallback(async (job) => {
    const data = await refreshActiveJob(job.id);
    setCheckoutError('');
    setLineItems(data.line_items || [{ description: data.service_category || data.title, quantity: 1, unit_price: data.estimated_value || 0 }]);
    setPaymentStatus(null);
    setPaymentMessage('');
    setPaymentError('');
    fetchPaymentStatus(data.id);
    try {
      const { data: photos } = await api.get('/photos', { params: { entity_type: 'job', entity_id: data.id } });
      setJobPhotos(photos);
    } catch {
      setJobPhotos([]);
    }
  }, [fetchPaymentStatus, refreshActiveJob]);

  const beforePhotoCount = jobPhotos.filter(photo => photo.photo_stage === 'before').length;
  const afterPhotoCount = jobPhotos.filter(photo => photo.photo_stage === 'after').length;
  const closeoutReady = beforePhotoCount >= 1 && afterPhotoCount >= 1;
  const jobs = useMemo(() => route?.jobs || [], [route]);
  const incompleteMileageJobIds = useMemo(() => new Set(mileageLogs.filter(log => (log.mileage_complete === false || log.odometer_end == null) && log.job_id).map(log => log.job_id)), [mileageLogs]);
  const scheduled = useMemo(() => jobs.filter(job => ['tentative', 'scheduled'].includes(job.status)), [jobs]);
  const inProgress = useMemo(() => jobs.filter(job => job.status === 'in_progress'), [jobs]);
  const completed = useMemo(() => jobs.filter(job => job.status === 'completed'), [jobs]);

  const routeData = useMemo(() => ({ user, viewDate, route, loading, jobs, scheduled, inProgress, completed, incompleteMileageJobIds, gpsMileage }), [completed, gpsMileage, incompleteMileageJobIds, inProgress, jobs, loading, route, scheduled, user, viewDate]);
  const routeActions = useMemo(() => ({ logout, setViewDate, setShowExpense, setShowMileage, openJob, checkin, startGpsMileage, stopGpsMileage }), [checkin, logout, openJob, startGpsMileage, stopGpsMileage]);
  const dialogData = useMemo(() => ({ activeJob, checkoutError, etaStatus, etaInput, notifyCustomerEta, currentEtaStatus, currentEtaSmsResult, beforePhotoCount, afterPhotoCount, closeoutReady, checkoutNotes, actualCosts, noteText, jobPhotos, showLineItems, lineItems, lineItemTotal, showSms, smsText, showExpense, expenseError, expenseSubmitting, showMileage, mileageError, mileageSubmitting, expenses, mileageLogs, mileageEndValues, gpsMileage, dateStr, paymentStatus, paymentLoading, paymentError, paymentMessage }), [activeJob, actualCosts, afterPhotoCount, beforePhotoCount, checkoutError, checkoutNotes, closeoutReady, currentEtaSmsResult, currentEtaStatus, dateStr, etaInput, etaStatus, expenseError, expenseSubmitting, expenses, gpsMileage, jobPhotos, lineItemTotal, lineItems, mileageEndValues, mileageError, mileageLogs, mileageSubmitting, noteText, notifyCustomerEta, paymentError, paymentLoading, paymentMessage, paymentStatus, showExpense, showLineItems, showMileage, showSms, smsText]);
  const dialogActions = useMemo(() => ({ api, setActiveJob, setCheckoutError, setJobPhotos, setEtaInput, setNotifyCustomerEta, sendOnMyWay, checkin, setCheckoutNotes, updateActualCost, setShowLineItems, setNoteText, addNote, handleJobPhotosChange, setLineItems, saveLineItems, setShowSms, setSmsText, sendSmsToCustomer, setShowExpense, submitExpense, setExpenseError, setShowMileage, submitMileage, setMileageError, setMileageEndValue, saveMileageEnd, startGpsMileage, stopGpsMileage, recordPayment, fetchPaymentStatus }), [addNote, checkin, fetchPaymentStatus, handleJobPhotosChange, recordPayment, saveLineItems, saveMileageEnd, sendOnMyWay, sendSmsToCustomer, setMileageEndValue, startGpsMileage, stopGpsMileage, submitExpense, submitMileage, updateActualCost]);
  const staffHelpers = useMemo(() => ({ statusColors }), []);

  return { routeData, routeActions, dialogData, dialogActions, staffHelpers };
}